import React, { FC, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { nearestPointIndex } from '../../../../shared/charts/chart-utils';
import { TimeSeriesAreaChartRecharts } from '../../../../shared/charts/recharts';
import {
  buildValueLabelRow,
  formatUvIndexChartLabel,
} from '../../helpers/graph/graphData';
import {
  buildUvFillGradientStops,
  buildUvLineGradientStops,
  getUvIndexColor,
  getUvIndexLineColor,
  getUvIndexYDomain,
} from '../../helpers/graph/uvIndexGradient';
import {
  WEATHER_BEZIER_AREA_CURVE,
  WEATHER_CHART_CHROME_COLORS,
  WEATHER_CHART_PLOT_INTERACTION_PROPS,
  WEATHER_CHART_PLOT_HEIGHT,
  WEATHER_CHART_SHOW_BACKGROUND_GRADIENT,
  buildWeatherChartBands,
  createWeatherChartXTickFormatter,
  type WeatherChartBaseProps,
} from './chartDefaults';

export type UVChartProps = WeatherChartBaseProps;

const UVChart: FC<UVChartProps> = ({
  data,
  domain,
  timeZone,
  selectedTime,
  defaultSelectedTime,
  onSelectedTimeChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const theme = useTheme();

  const backgroundBands = useMemo(
    () => buildWeatherChartBands(domain, timeZone),
    [domain, timeZone],
  );

  const labelRows = useMemo(
    () => [buildValueLabelRow(data, formatUvIndexChartLabel)],
    [data],
  );

  const yDomain = useMemo(
    () => getUvIndexYDomain(data.map((point) => point.value)),
    [data],
  );

  const fillGradientStops = useMemo(() => buildUvFillGradientStops(), []);

  const lineGradientStops = useMemo(() => buildUvLineGradientStops(), []);

  const scrubberDotColor = useMemo(() => {
    const time = selectedTime ?? defaultSelectedTime;
    if (time == null || data.length === 0) {
      return getUvIndexColor(yDomain[1]);
    }

    const index = nearestPointIndex(data, time);
    const value = data[index]?.value;
    return value != null ? getUvIndexColor(value) : getUvIndexColor(yDomain[1]);
  }, [data, defaultSelectedTime, selectedTime, yDomain]);

  const formatXTick = useMemo(
    () => createWeatherChartXTickFormatter(timeZone),
    [timeZone],
  );

  return (
    <TimeSeriesAreaChartRecharts
      data={data}
      domain={domain}
      timeZone={timeZone}
      curve={WEATHER_BEZIER_AREA_CURVE}
      colors={{
        line: getUvIndexLineColor(yDomain[1]),
        fill: {
          top: getUvIndexColor(yDomain[1]),
          bottom: getUvIndexColor(0),
        },
        scrubberDot: scrubberDotColor,
      }}
      fillGradientStops={fillGradientStops}
      lineGradientStops={lineGradientStops}
      plotHeight={WEATHER_CHART_PLOT_HEIGHT}
      xTickIntervalHours={3}
      hideMidnightTicks
      formatXTick={formatXTick}
      xTickColor={theme.colors.darkergrey}
      backgroundBands={backgroundBands}
      showBackgroundGradient={WEATHER_CHART_SHOW_BACKGROUND_GRADIENT}
      labelRows={labelRows}
      yDomain={yDomain}
      selectedTime={selectedTime}
      defaultSelectedTime={defaultSelectedTime}
      onSelectedTimeChange={onSelectedTimeChange}
      onScrubStart={onScrubStart}
      onScrubEnd={onScrubEnd}
      plotInteractionProps={WEATHER_CHART_PLOT_INTERACTION_PROPS}
      chromeColors={WEATHER_CHART_CHROME_COLORS}
    />
  );
};

export default UVChart;
